import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageSkeleton from '../../components/dashboard/PageSkeleton';
import {
  Loader2, Save, CheckCircle2, AlertCircle, ToggleLeft, ToggleRight,
  Shield, ShieldAlert, ShieldOff, MessageSquare, AtSign, Link2, Type,
  Hash, X, Plus, Trash2, Bomb, AlertTriangle
} from 'lucide-react';

interface ModerationConfigProps {
  guildId: string;
  guildData: any;
}

interface SubConfig {
  enabled: boolean;
  [key: string]: any;
}

interface ModConfig {
  guild_id: string;
  automod_enabled: boolean;
  log_channels: string[];
  channel_detection_enabled: boolean;
  detection_channels: string[];
  ignored_channels: string[];
  ignored_roles: string[];
  blocked_words: string[];
  anti_spam: SubConfig;
  anti_invite: SubConfig;
  anti_caps: SubConfig;
  anti_mention: SubConfig;
  word_filter: SubConfig;
  anti_links: SubConfig;
  anti_nuke: SubConfig;
}

const DEFAULT_CONFIG: ModConfig = {
  guild_id: '',
  automod_enabled: false,
  log_channels: [],
  channel_detection_enabled: false,
  detection_channels: [],
  ignored_channels: [],
  ignored_roles: [],
  blocked_words: [],
  anti_spam: { enabled: false, message_limit: 5, time_window: 5, action: 'timeout', duration: '5m' },
  anti_invite: { enabled: false, action: 'delete', whitelist: [] },
  anti_caps: { enabled: false, threshold: 70, min_length: 10, action: 'delete' },
  anti_mention: { enabled: false, max_mentions: 5, action: 'timeout', duration: '10m' },
  word_filter: { enabled: false, words: [], action: 'delete' },
  anti_links: { enabled: false, action: 'delete', whitelist_domains: [] },
  anti_nuke: { enabled: false, channel_delete_limit: 3, role_delete_limit: 3, ban_limit: 5, time_window: 10, action: 'strip_roles' },
};

const ACTIONS = [
  { value: 'delete', label: 'Delete Message' },
  { value: 'timeout', label: 'Timeout User' },
  { value: 'mute', label: 'Mute User' },
  { value: 'kick', label: 'Kick User' },
  { value: 'ban', label: 'Ban User' },
];

const NUKE_ACTIONS = [
  { value: 'strip_roles', label: 'Strip All Roles' },
  { value: 'ban', label: 'Ban Attacker' },
  { value: 'kick', label: 'Kick Attacker' },
];

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!enabled)} className="transition-colors shrink-0">
      {enabled ? <ToggleRight className="w-7 h-7 text-primary" /> : <ToggleLeft className="w-7 h-7 text-gray-700" />}
    </button>
  );
}

function SectionCard({
  icon: Icon, title, description, enabled, onToggle, children,
}: {
  icon: any; title: string; description: string; enabled: boolean; onToggle: (v: boolean) => void; children?: React.ReactNode;
}) {
  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${
      enabled ? 'bg-dark-800/40 border-white/[0.06]' : 'bg-dark-800/20 border-white/[0.03]'
    }`}>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-md ${enabled ? 'bg-primary/10 text-primary' : 'bg-dark-700/60 text-gray-600'}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <div>
            <p className={`text-[13px] font-medium ${enabled ? 'text-white' : 'text-gray-400'}`}>{title}</p>
            <p className="text-gray-600 text-[11px]">{description}</p>
          </div>
        </div>
        <Toggle enabled={enabled} onChange={onToggle} />
      </div>
      {enabled && children && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.04] space-y-2.5">
          {children}
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-2 items-center">
      <label className="text-[11px] text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, min, max }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="w-20 px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] focus:outline-none focus:border-primary/40 transition-colors"
    />
  );
}

function SelectInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] focus:outline-none focus:border-primary/40 transition-colors appearance-none"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function ChipList({ items, onRemove, emptyText }: { items: string[]; onRemove: (i: number) => void; emptyText: string }) {
  if (items.length === 0) return <p className="text-gray-700 text-[10px]">{emptyText}</p>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, i) => (
        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-dark-700/80 border border-white/[0.05] rounded text-[11px] text-gray-300">
          {item}
          <button type="button" onClick={() => onRemove(i)} className="text-gray-600 hover:text-red-400 transition-colors">
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
    </div>
  );
}

function AddInput({ placeholder, onAdd }: { placeholder: string; onAdd: (v: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <div className="flex gap-1.5">
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && val.trim()) { onAdd(val.trim()); setVal(''); } }}
        placeholder={placeholder}
        className="flex-1 px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] placeholder-gray-600 focus:outline-none focus:border-primary/40 transition-colors"
      />
      <button
        type="button"
        onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(''); } }}
        className="px-2.5 py-1.5 bg-primary/10 border border-primary/20 rounded-md text-primary text-[11px] hover:bg-primary/20 transition-colors"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function ChannelMultiSelect({ 
  channels, 
  selected, 
  onChange, 
  label, 
  description 
}: { 
  channels: any[]; 
  selected: string[]; 
  onChange: (ids: string[]) => void; 
  label: string; 
  description: string; 
}) {
  const [manualId, setManualId] = useState('');
  
  const addChannel = (id: string) => {
    if (id && !selected.includes(id)) {
      onChange([...selected, id]);
    }
  };
  
  const removeChannel = (id: string) => {
    onChange(selected.filter(cid => cid !== id));
  };
  
  return (
    <div className="bg-dark-800/40 border border-white/[0.05] rounded-lg p-4 space-y-3">
      <div>
        <p className="text-white text-[13px] font-medium">{label}</p>
        <p className="text-gray-600 text-[11px]">{description}</p>
      </div>
      
      {/* Dropdown selector */}
      <select
        value=""
        onChange={(e) => {
          addChannel(e.target.value);
          e.target.value = '';
        }}
        className="w-full px-3 py-2 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[13px] focus:outline-none focus:border-primary/40 transition-colors appearance-none"
      >
        <option value="">Select a channel...</option>
        {channels.filter((ch: any) => !selected.includes(ch.id)).map((ch: any) => (
          <option key={ch.id} value={ch.id}>#{ch.name}</option>
        ))}
      </select>
      
      {/* Manual ID input */}
      <div className="flex gap-1.5">
        <input
          type="text"
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && manualId.trim()) {
              addChannel(manualId.trim());
              setManualId('');
            }
          }}
          placeholder="Or paste channel ID..."
          className="flex-1 px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] placeholder-gray-600 focus:outline-none focus:border-primary/40 transition-colors font-mono"
        />
        <button
          type="button"
          onClick={() => {
            if (manualId.trim()) {
              addChannel(manualId.trim());
              setManualId('');
            }
          }}
          className="px-2.5 py-1.5 bg-primary/10 border border-primary/20 rounded-md text-primary text-[11px] hover:bg-primary/20 transition-colors"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
      
      {/* Selected channels */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((chId) => {
            const ch = channels.find((c: any) => c.id === chId);
            return (
              <span key={chId} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-dark-700/80 border border-white/[0.05] rounded text-[12px] text-gray-300">
                <Hash className="w-3 h-3 text-gray-500" />
                {ch?.name || chId}
                <button
                  type="button"
                  onClick={() => removeChannel(chId)}
                  className="text-gray-600 hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      
      {selected.length === 0 && (
        <p className="text-gray-700 text-[11px] text-center py-2">No channels selected</p>
      )}
    </div>
  );
}

export default function ModerationConfig({ guildId, guildData }: ModerationConfigProps) {
  const { token } = useAuth();
  const [config, setConfig] = useState<ModConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'protection' | 'words' | 'antinuke' | 'settings'>('protection');

  const channels = guildData?.channels || [];
  const roles = guildData?.roles || [];

  useEffect(() => {
    if (!guildId || !token) return;
    async function fetchConfig() {
      try {
        const API_BASE = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/moderation`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setConfig({ ...DEFAULT_CONFIG, ...data });
        }
      } catch (err) {
        console.error('Failed to fetch moderation config:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchConfig();
  }, [guildId, token]);

  function updateSub(key: keyof ModConfig, field: string, value: any) {
    setConfig((c) => ({
      ...c,
      [key]: { ...(c[key] as any), [field]: value },
    }));
  }

  async function handleSave() {
    if (!token) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const res = await fetch(`${API_BASE}/api/dashboard/${guildId}/moderation`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      setSaveStatus(res.ok ? 'success' : 'error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !guildData) {
    return <PageSkeleton rows={5} />;
  }

  const tabs = [
    { id: 'protection', label: 'Protection' },
    { id: 'words', label: `Blocked Words (${config.blocked_words.length})` },
    { id: 'antinuke', label: 'Anti-Nuke' },
    { id: 'settings', label: 'Settings' },
  ] as const;

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-display font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Moderation
          </h2>
          <p className="text-gray-500 text-[13px] mt-0.5">Auto-moderation, anti-spam, word filter & anti-nuke</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg font-medium text-[13px] transition-all active:scale-[0.97] ${
            saveStatus === 'success' ? 'bg-green-500/15 text-green-400 border border-green-500/25'
            : saveStatus === 'error' ? 'bg-red-500/15 text-red-400 border border-red-500/25'
            : 'bg-primary hover:bg-primary-dark text-white'
          }`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saveStatus === 'success' ? <CheckCircle2 className="w-3.5 h-3.5" /> : saveStatus === 'error' ? <AlertCircle className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Failed' : 'Save'}
        </button>
      </div>

      {/* Master toggle */}
      <button type="button" onClick={() => setConfig((c) => ({ ...c, automod_enabled: !c.automod_enabled }))}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all text-left ${
          config.automod_enabled ? 'bg-green-500/5 border-green-500/20' : 'bg-dark-800/40 border-white/[0.04]'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-md ${config.automod_enabled ? 'bg-green-500/10 text-green-400' : 'bg-dark-700/60 text-gray-600'}`}>
            {config.automod_enabled ? <ShieldAlert className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
          </div>
          <div>
            <p className={`text-[13px] font-medium ${config.automod_enabled ? 'text-white' : 'text-gray-400'}`}>AutoMod System</p>
            <p className="text-gray-600 text-[11px]">Master toggle for all auto-moderation</p>
          </div>
        </div>
        {config.automod_enabled ? <ToggleRight className="w-7 h-7 text-green-400 shrink-0" /> : <ToggleLeft className="w-7 h-7 text-gray-700 shrink-0" />}
      </button>

      {/* Tabs — underline style */}
      <div className="flex gap-0.5 border-b border-white/[0.06] overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-[13px] font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === tab.id ? 'border-primary text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── Protection Tab ─── */}
      {activeTab === 'protection' && (
        <div className="space-y-3">
          {/* Anti-Spam */}
          <SectionCard
            icon={MessageSquare}
            title="Anti-Spam"
            description="Detect and punish message spam"
            enabled={config.anti_spam.enabled}
            onToggle={(v) => updateSub('anti_spam', 'enabled', v)}
          >
            <FieldRow label="Max Messages">
              <NumberInput value={config.anti_spam.message_limit} onChange={(v) => updateSub('anti_spam', 'message_limit', v)} min={2} max={20} />
            </FieldRow>
            <FieldRow label="Time Window">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_spam.time_window} onChange={(v) => updateSub('anti_spam', 'time_window', v)} min={1} max={30} />
                <span className="text-gray-500 text-xs">seconds</span>
              </div>
            </FieldRow>
            <FieldRow label="Action">
              <SelectInput value={config.anti_spam.action} onChange={(v) => updateSub('anti_spam', 'action', v)} options={ACTIONS} />
            </FieldRow>
            <FieldRow label="Duration">
              <input
                type="text"
                value={config.anti_spam.duration}
                onChange={(e) => updateSub('anti_spam', 'duration', e.target.value)}
                placeholder="e.g. 5m, 1h, 1d"
                className="w-20 px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] focus:outline-none focus:border-primary/40"
              />
            </FieldRow>
          </SectionCard>

          {/* Anti-Invite */}
          <SectionCard
            icon={Link2}
            title="Anti-Invite"
            description="Block Discord invite links"
            enabled={config.anti_invite.enabled}
            onToggle={(v) => updateSub('anti_invite', 'enabled', v)}
          >
            <FieldRow label="Action">
              <SelectInput value={config.anti_invite.action} onChange={(v) => updateSub('anti_invite', 'action', v)} options={ACTIONS} />
            </FieldRow>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Whitelisted Servers</label>
              <ChipList
                items={config.anti_invite.whitelist || []}
                onRemove={(i) => {
                  const updated = [...(config.anti_invite.whitelist || [])];
                  updated.splice(i, 1);
                  updateSub('anti_invite', 'whitelist', updated);
                }}
                emptyText="No whitelisted servers"
              />
              <div className="mt-2">
                <AddInput
                  placeholder="Server invite code..."
                  onAdd={(v) => updateSub('anti_invite', 'whitelist', [...(config.anti_invite.whitelist || []), v])}
                />
              </div>
            </div>
          </SectionCard>

          {/* Anti-Links */}
          <SectionCard
            icon={Link2}
            title="Anti-Links"
            description="Block external links"
            enabled={config.anti_links.enabled}
            onToggle={(v) => updateSub('anti_links', 'enabled', v)}
          >
            <FieldRow label="Action">
              <SelectInput value={config.anti_links.action} onChange={(v) => updateSub('anti_links', 'action', v)} options={ACTIONS} />
            </FieldRow>
            <div>
              <label className="text-xs text-gray-400 mb-1.5 block">Whitelisted Domains</label>
              <ChipList
                items={config.anti_links.whitelist_domains || []}
                onRemove={(i) => {
                  const updated = [...(config.anti_links.whitelist_domains || [])];
                  updated.splice(i, 1);
                  updateSub('anti_links', 'whitelist_domains', updated);
                }}
                emptyText="No whitelisted domains"
              />
              <div className="mt-2">
                <AddInput
                  placeholder="e.g. youtube.com"
                  onAdd={(v) => updateSub('anti_links', 'whitelist_domains', [...(config.anti_links.whitelist_domains || []), v])}
                />
              </div>
            </div>
          </SectionCard>

          {/* Anti-Caps */}
          <SectionCard
            icon={Type}
            title="Anti-Caps"
            description="Block excessive CAPS messages"
            enabled={config.anti_caps.enabled}
            onToggle={(v) => updateSub('anti_caps', 'enabled', v)}
          >
            <FieldRow label="Threshold">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_caps.threshold} onChange={(v) => updateSub('anti_caps', 'threshold', v)} min={50} max={100} />
                <span className="text-gray-500 text-xs">% caps</span>
              </div>
            </FieldRow>
            <FieldRow label="Min Length">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_caps.min_length} onChange={(v) => updateSub('anti_caps', 'min_length', v)} min={3} max={100} />
                <span className="text-gray-500 text-xs">chars</span>
              </div>
            </FieldRow>
            <FieldRow label="Action">
              <SelectInput value={config.anti_caps.action} onChange={(v) => updateSub('anti_caps', 'action', v)} options={ACTIONS} />
            </FieldRow>
          </SectionCard>

          {/* Anti-Mention Spam */}
          <SectionCard
            icon={AtSign}
            title="Anti-Mention Spam"
            description="Limit mass mentions in a single message"
            enabled={config.anti_mention.enabled}
            onToggle={(v) => updateSub('anti_mention', 'enabled', v)}
          >
            <FieldRow label="Max Mentions">
              <NumberInput value={config.anti_mention.max_mentions} onChange={(v) => updateSub('anti_mention', 'max_mentions', v)} min={1} max={50} />
            </FieldRow>
            <FieldRow label="Action">
              <SelectInput value={config.anti_mention.action} onChange={(v) => updateSub('anti_mention', 'action', v)} options={ACTIONS} />
            </FieldRow>
            <FieldRow label="Duration">
              <input
                type="text"
                value={config.anti_mention.duration}
                onChange={(e) => updateSub('anti_mention', 'duration', e.target.value)}
                placeholder="e.g. 10m"
                className="w-20 px-2.5 py-1.5 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[12px] focus:outline-none focus:border-primary/40"
              />
            </FieldRow>
          </SectionCard>
        </div>
      )}

      {/* ─── Blocked Words Tab ─── */}
      {activeTab === 'words' && (
        <div className="bg-dark-800/40 border border-white/[0.05] rounded-lg p-4 space-y-3">
          <div>
            <p className="text-white text-[13px] font-medium">Word Filter</p>
            <p className="text-gray-600 text-[11px]">Messages containing these words will be automatically deleted.</p>
          </div>

          <AddInput
            placeholder="Add a blocked word or phrase..."
            onAdd={(v) => {
              const word = v.toLowerCase();
              if (!config.blocked_words.includes(word)) {
                setConfig((c) => ({ ...c, blocked_words: [...c.blocked_words, word] }));
              }
            }}
          />

          {config.blocked_words.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {config.blocked_words.map((word, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/15 rounded text-[11px] text-red-300">
                  {word}
                  <button
                    type="button"
                    onClick={() => setConfig((c) => ({ ...c, blocked_words: c.blocked_words.filter((_, idx) => idx !== i) }))}
                    className="text-red-400/50 hover:text-red-300 transition-colors"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-700 text-[11px] text-center py-4">No blocked words yet</p>
          )}

          {config.blocked_words.length > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-white/[0.04]">
              <p className="text-gray-600 text-[11px]">{config.blocked_words.length} word(s)</p>
              <button type="button" onClick={() => setConfig((c) => ({ ...c, blocked_words: [] }))}
                className="flex items-center gap-1 text-[11px] text-red-400/70 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Anti-Nuke Tab ─── */}
      {activeTab === 'antinuke' && (
        <div className="space-y-3">
          <SectionCard
            icon={Bomb}
            title="Anti-Nuke Protection"
            description="Detect mass channel/role deletions and mass bans — automatically punish the attacker"
            enabled={config.anti_nuke.enabled}
            onToggle={(v) => updateSub('anti_nuke', 'enabled', v)}
          >
            <FieldRow label="Channel Deletes">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_nuke.channel_delete_limit} onChange={(v) => updateSub('anti_nuke', 'channel_delete_limit', v)} min={1} max={20} />
                <span className="text-gray-500 text-xs">max before action</span>
              </div>
            </FieldRow>
            <FieldRow label="Role Deletes">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_nuke.role_delete_limit} onChange={(v) => updateSub('anti_nuke', 'role_delete_limit', v)} min={1} max={20} />
                <span className="text-gray-500 text-xs">max before action</span>
              </div>
            </FieldRow>
            <FieldRow label="Mass Bans">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_nuke.ban_limit} onChange={(v) => updateSub('anti_nuke', 'ban_limit', v)} min={1} max={30} />
                <span className="text-gray-500 text-xs">max before action</span>
              </div>
            </FieldRow>
            <FieldRow label="Time Window">
              <div className="flex items-center gap-2">
                <NumberInput value={config.anti_nuke.time_window} onChange={(v) => updateSub('anti_nuke', 'time_window', v)} min={5} max={60} />
                <span className="text-gray-500 text-xs">seconds</span>
              </div>
            </FieldRow>
            <FieldRow label="Action">
              <SelectInput value={config.anti_nuke.action} onChange={(v) => updateSub('anti_nuke', 'action', v)} options={NUKE_ACTIONS} />
            </FieldRow>
          </SectionCard>

          <div className="bg-amber-500/5 border border-amber-500/15 rounded-md px-3 py-2.5">
            <p className="text-amber-300/80 text-[11px] flex items-start gap-2 leading-relaxed">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Monitors audit log for rapid destructive actions. The server owner is always exempt.
            </p>
          </div>
        </div>
      )}

      {/* ─── Settings Tab ─── */}
      {activeTab === 'settings' && (
        <div className="space-y-3">
          {/* Log Channels */}
          <ChannelMultiSelect
            channels={channels}
            selected={config.log_channels}
            onChange={(ids) => setConfig((c) => ({ ...c, log_channels: ids }))}
            label="Mod Log Channels"
            description="All auto-mod actions will be logged to these channels. You can select multiple or paste channel IDs."
          />

          {/* Channel Detection */}
          <div className={`border rounded-lg overflow-hidden transition-all ${
            config.channel_detection_enabled ? 'bg-dark-800/40 border-white/[0.06]' : 'bg-dark-800/20 border-white/[0.03]'
          }`}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-md ${config.channel_detection_enabled ? 'bg-primary/10 text-primary' : 'bg-dark-700/60 text-gray-600'}`}>
                  <Hash className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className={`text-[13px] font-medium ${config.channel_detection_enabled ? 'text-white' : 'text-gray-400'}`}>Channel Detection</p>
                  <p className="text-gray-600 text-[11px]">{config.channel_detection_enabled ? 'Monitoring specific channels' : 'Monitoring all channels (default)'}</p>
                </div>
              </div>
              <Toggle enabled={config.channel_detection_enabled} onChange={(v) => setConfig((c) => ({ ...c, channel_detection_enabled: v }))} />
            </div>
            {config.channel_detection_enabled && (
              <div className="px-4 pb-4 pt-1 border-t border-white/[0.04] space-y-4">
                {/* Detect Channels */}
                <div className="space-y-2">
                  <div>
                    <p className="text-green-300 text-[12px] font-medium">Channels to Detect</p>
                    <p className="text-gray-600 text-[10px]">Auto-mod will ONLY run in these channels. Leave empty = all channels.</p>
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !config.detection_channels.includes(e.target.value)) {
                        setConfig((c) => ({ ...c, detection_channels: [...c.detection_channels, e.target.value] }));
                      }
                      e.target.value = '';
                    }}
                    className="w-full px-3 py-2 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[13px] focus:outline-none focus:border-primary/40 transition-colors appearance-none"
                  >
                    <option value="">Select a channel...</option>
                    {channels.filter((ch: any) => !config.detection_channels.includes(ch.id)).map((ch: any) => (
                      <option key={ch.id} value={ch.id}>#{ch.name}</option>
                    ))}
                  </select>
                  <AddInput placeholder="Or paste channel ID..." onAdd={(v) => {
                    if (!config.detection_channels.includes(v)) setConfig((c) => ({ ...c, detection_channels: [...c.detection_channels, v] }));
                  }} />
                  {config.detection_channels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {config.detection_channels.map((chId) => {
                        const ch = channels.find((c: any) => c.id === chId);
                        return (
                          <span key={chId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 border border-green-500/20 rounded text-[11px] text-green-300">
                            <Hash className="w-2.5 h-2.5" />
                            {ch?.name || chId}
                            <button type="button" onClick={() => setConfig((c) => ({ ...c, detection_channels: c.detection_channels.filter((id) => id !== chId) }))}
                              className="text-green-400/50 hover:text-green-300 transition-colors">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-700 text-[10px] text-center py-2 bg-dark-900/40 rounded border border-white/[0.03]">All channels monitored</p>
                  )}
                </div>

                <div className="border-t border-white/[0.04]"></div>

                {/* Ignore Channels */}
                <div className="space-y-2">
                  <div>
                    <p className="text-red-300 text-[12px] font-medium">Channels to Ignore</p>
                    <p className="text-gray-600 text-[10px]">Auto-mod will NEVER run in these channels.</p>
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !config.ignored_channels.includes(e.target.value)) {
                        setConfig((c) => ({ ...c, ignored_channels: [...c.ignored_channels, e.target.value] }));
                      }
                      e.target.value = '';
                    }}
                    className="w-full px-3 py-2 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[13px] focus:outline-none focus:border-primary/40 transition-colors appearance-none"
                  >
                    <option value="">Select a channel...</option>
                    {channels.filter((ch: any) => !config.ignored_channels.includes(ch.id)).map((ch: any) => (
                      <option key={ch.id} value={ch.id}>#{ch.name}</option>
                    ))}
                  </select>
                  <AddInput placeholder="Or paste channel ID..." onAdd={(v) => {
                    if (!config.ignored_channels.includes(v)) setConfig((c) => ({ ...c, ignored_channels: [...c.ignored_channels, v] }));
                  }} />
                  {config.ignored_channels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {config.ignored_channels.map((chId) => {
                        const ch = channels.find((c: any) => c.id === chId);
                        return (
                          <span key={chId} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/15 rounded text-[11px] text-red-300">
                            <Hash className="w-2.5 h-2.5" />
                            {ch?.name || chId}
                            <button type="button" onClick={() => setConfig((c) => ({ ...c, ignored_channels: c.ignored_channels.filter((id) => id !== chId) }))}
                              className="text-red-400/50 hover:text-red-300 transition-colors">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-700 text-[10px] text-center py-2">No channels ignored</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Ignored Roles */}
          <div className="bg-dark-800/40 border border-white/[0.05] rounded-lg p-4 space-y-2">
            <p className="text-white text-[13px] font-medium">Ignored Roles</p>
            <p className="text-gray-600 text-[11px]">Members with these roles are exempt from auto-mod.</p>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value && !config.ignored_roles.includes(e.target.value)) {
                  setConfig((c) => ({ ...c, ignored_roles: [...c.ignored_roles, e.target.value] }));
                }
                e.target.value = '';
              }}
              className="w-full px-3 py-2 bg-dark-800 border border-white/[0.06] rounded-md text-white text-[13px] focus:outline-none focus:border-primary/40 transition-colors appearance-none"
            >
              <option value="">Add a role...</option>
              {roles.filter((r: any) => !config.ignored_roles.includes(r.id)).map((r: any) => (
                <option key={r.id} value={r.id}>@{r.name}</option>
              ))}
            </select>
            {config.ignored_roles.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {config.ignored_roles.map((rId) => {
                  const role = roles.find((r: any) => r.id === rId);
                  return (
                    <span key={rId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 border rounded text-[11px]"
                      style={{
                        backgroundColor: role?.color ? role.color + '12' : 'rgb(55 55 62 / 0.8)',
                        borderColor: role?.color ? role.color + '35' : 'rgba(255,255,255,0.05)',
                        color: role?.color || '#9ca3af',
                      }}
                    >
                      @{role?.name || rId}
                      <button type="button" onClick={() => setConfig((c) => ({ ...c, ignored_roles: c.ignored_roles.filter((id) => id !== rId) }))}
                        className="opacity-60 hover:opacity-100 hover:text-red-400 transition-all"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
